import { useContext, useState } from 'react';
import { CreateProjectContext, SUPPORT_TYPE_DETAILS, SUPPORT_TYPES, SupportType } from './consts';
import { Accordion, Button, CheckBox, HelpIcon, Icon } from '@platform/components';
import { ROUTES } from '../routes/consts';
import dataTestids from '../constants/test-ids';
import { navigate } from 'wouter/use-browser-location';

const SupportTypeStep = () => {
  const { supportTypes, setSupportTypes } = useContext(CreateProjectContext);
  const [selectedSupportTypes, setSelectedSupportTypes] = useState<Set<string>>(
    new Set(supportTypes),
  );

  const onSubmit = () => {
    setSupportTypes(selectedSupportTypes as Set<SupportType>);
    navigate(`/platform${ROUTES.CREATE_PROJECT}/4`);
  };

  return (
    <>
      <div className='text-center'>
        <div>What type of support are you looking for? Select one or more types.</div>
        <div>
          The professionals in our network can support you in different ways, over different time
          frames.
        </div>
      </div>
      <div className='flex flex-col gap-2'>
        {SUPPORT_TYPES.map((supportType) => {
          const supportTypeDetails = SUPPORT_TYPE_DETAILS[supportType];
          return (
            <div
              className='flex bg-white p-3 text-left'
              data-testid={dataTestids.CREATE_PROJECT.SUPPORT_TYPE_ROW}
              key={supportType}
            >
              <div className='flex flex-col gap-1'>
                <span className='flex items-center gap-2 font-semibold'>
                  {supportType}
                  <HelpIcon supportingText={supportTypeDetails.tooltip} />
                </span>
                <span>{supportTypeDetails.description}</span>
                <Accordion
                  type='custom'
                  isBorderless
                  initiallyClosed
                  titleContent={<span>See examples</span>}
                >
                  <div className='flex gap-2 bg-gray-50 rounded-md p-2'>
                    {supportTypeDetails.examples.map((detailsItem) => (
                      <div className='w-[288px] p-2 bg-white flex flex-col gap-2 shadow-lg rounded-md'>
                        <div className='flex gap-2'>
                          <Icon type='users-02' size='24' />
                          <span className='capitalize'>{supportType}</span>
                        </div>
                        <span className='font-semibold'>{detailsItem.title}</span>
                        <span className='w-20 h-[2px] bg-accent-500'></span>
                        <span className='text-sm'>{detailsItem.description}</span>
                        <div className='flex gap-4 mt-auto'>
                          <div className='flex flex-col items-center self-start'>
                            <Icon type='marker-pin-01' size='20' />
                            <span className='text-sm'>{detailsItem.country}</span>
                          </div>
                          {detailsItem.isRemote && (
                            <div className='flex flex-col items-center self-start'>
                              <Icon type='home-01' size='20' />
                              <span className='text-sm'>Remote</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Accordion>
              </div>
              <div className='ml-auto'>
                <CheckBox
                  aria-label={`Select support type ${supportType}`}
                  dataTestId={dataTestids.CREATE_PROJECT.SUPPORT_TYPE_SELECT_BUTTON}
                  isSelected={selectedSupportTypes.has(supportType)}
                  onChange={(isSelected) => {
                    setSelectedSupportTypes((prev) => {
                      const newSet = new Set(prev);
                      if (isSelected) {
                        newSet.add(supportType);
                      } else {
                        newSet.delete(supportType);
                      }
                      return newSet;
                    });
                  }}
                  description='Select'
                  size='md'
                />
              </div>
            </div>
          );
        })}
        <div className='w-fit mx-auto'>
          <Button
            variant='accent'
            size='lg'
            onPress={onSubmit}
            isDisabled={selectedSupportTypes.size === 0}
          >
            Save and continue
          </Button>
        </div>
      </div>
    </>
  );
};

export default SupportTypeStep;
